import { guessCategory } from "../categories/guess.js";
import { guessStaple } from "../categories/staples.js";
import type { CategoryKey } from "../categories/generated.js";
import { normalizeName } from "../parser/item-input.js";
import type { IsoTimestamp, Uuid } from "../types/base.js";
import type { Recipe, RecipeIngredient, Unit } from "../types/entities.js";

/**
 * Abbildung der Spaisekammer auf die Rezepte dieser App.
 *
 * Reine Funktionen ohne Netz und ohne Datenbank: Die Edge Function ruft sie,
 * ein späterer Rückweg soll dieselbe Abbildung benutzen, und die Fälle, auf
 * die es ankommt, lassen sich in Millisekunden durchtesten.
 */

// --- Was von drüben kommt ----------------------------------------------------

export interface SpaisekammerZutat {
  id: string;
  position: number;
  group_name: string | null;
  amount: number | null;
  amount_max: number | null;
  unit: string | null;
  item: string;
  preparation: string | null;
  note: string | null;
  optional: boolean;
  amount_text: string | null;
  shopping_category: string | null;
}

export interface SpaisekammerRezept {
  id: string;
  slug: string;
  title: string;
  subtitle: string | null;
  servings: number | null;
  servings_unit: string | null;
  updated_at: IsoTimestamp;
  owner_name: string;
  cover_url: string | null;
  source_url: string | null;
  ingredients: SpaisekammerZutat[];
}

// --- Einheiten ---------------------------------------------------------------

/**
 * Nur was hier steht, hat eine Entsprechung. Alles andere (EL, TL, Prise,
 * Zehe, Zweig, Blatt, Handvoll, Tasse, Scheibe, Msp) hat keine: Menge und
 * Einheit werden null, die Zutat steht ohne Menge auf der Liste.
 *
 * Das folgt der Entscheidung aus Stufe 5: Eine erfundene Zahl wäre schlimmer
 * als keine. Zwei Esslöffel Olivenöl kauft man als Flasche, und eine Zahl, die
 * niemand nachrechnen kann, zöge im Vorratsabgleich Mengen ab, die es nie gab.
 */
export const EINHEITEN: Readonly<Record<string, Unit>> = {
  g: "g",
  kg: "kg",
  ml: "ml",
  l: "l",
  stück: "stk",
  stueck: "stk",
  stk: "stk",
  packung: "pkg",
  pkg: "pkg",
  dose: "dose",
  glas: "glas",
  bund: "bund",
  becher: "becher",
  flasche: "flasche",
};

/** Einheit der Spaisekammer in eine unsere. Null heißt: keine Entsprechung. */
export function mapEinheit(unit: string | null): Unit | null {
  if (!unit) return null;
  return EINHEITEN[unit.trim().toLowerCase()] ?? null;
}

// --- Warengruppen ------------------------------------------------------------

/**
 * Die Spaisekammer kennt neun grobe Gruppen, wir dreiunddreißig feine. Was
 * drüben `other` oder leer ist, rät guessCategory aus dem Namen: Es kennt die
 * deutschen Zutaten schon.
 */
export const WARENGRUPPEN: Readonly<Record<string, CategoryKey>> = {
  produce: "obst_gemuese",
  dairy: "molkerei",
  meat: "fleisch_fisch",
  fish: "fleisch_fisch",
  bakery: "backwaren",
  frozen: "tiefkuehl",
  drinks: "getraenke",
  pantry: "trockensortiment",
  spices: "trockensortiment",
};

export function mapWarengruppe(shoppingCategory: string | null, name: string): CategoryKey | null {
  const treffer = shoppingCategory ? WARENGRUPPEN[shoppingCategory.trim().toLowerCase()] : undefined;
  return treffer ?? guessCategory(name);
}

// --- Portionen ---------------------------------------------------------------

export const PORTIONEN_MIN = 1;
export const PORTIONEN_MAX = 50;
export const PORTIONEN_STANDARD = 2;

/**
 * Die Einheit der Portionen (Portionen, Stück, Gläser) fällt weg: Der
 * Wochenplan rechnet mit einer Zahl. Sie wird gerundet und auf 1 bis 50
 * begrenzt, weil die Spalte das verlangt.
 */
export function mapPortionen(servings: number | null): number {
  if (servings === null || !Number.isFinite(servings)) return PORTIONEN_STANDARD;
  return Math.min(PORTIONEN_MAX, Math.max(PORTIONEN_MIN, Math.round(servings)));
}

/** Link "In der Spaisekammer öffnen". */
export function rezeptUrl(baseUrl: string, slug: string): string {
  return `${baseUrl.replace(/\/+$/, "")}/rezept/${slug}`;
}

// --- Der Abgleich eines Rezepts ---------------------------------------------

/** Zutat, die schon gespiegelt ist. is_staple gehört dem Gerät, nicht drüben. */
export interface BestehendeZutat {
  id: Uuid;
  external_id: string;
  is_staple: boolean;
}

export interface AbgleichEingabe {
  rezept: SpaisekammerRezept;
  familyId: Uuid;
  /** Vorhandene Zeile, falls dieses Rezept schon gespiegelt wurde. */
  bestehendesRezept: { id: Uuid } | null;
  bestehendeZutaten: readonly BestehendeZutat[];
  /** Neue Kennungen kommen von außen, der Kern erzeugt keine. */
  neueId: () => Uuid;
  now: IsoTimestamp;
  /** Adresse der Spaisekammer, ohne Schrägstrich am Ende. */
  baseUrl: string;
}

export interface AbgleichErgebnis {
  recipe: Recipe;
  ingredients: RecipeIngredient[];
  /** Zutaten, die es drüben nicht mehr gibt. Sie bekommen einen Grabstein. */
  entfernt: Uuid[];
}

/**
 * Baut die Zeilen für ein gespiegeltes Rezept.
 *
 * Idempotent: Zweimal mit demselben Stand gerufen, entstehen dieselben Zeilen
 * mit denselben Kennungen. Nur updated_at und client_updated_at wandern mit,
 * denn daran hängt die Konfliktregel.
 */
export function planSpaisekammerRezept(input: AbgleichEingabe): AbgleichErgebnis {
  const { rezept, familyId, bestehendesRezept, bestehendeZutaten, neueId, now, baseUrl } = input;

  const recipe: Recipe & Record<string, unknown> = {
    id: bestehendesRezept?.id ?? neueId(),
    family_id: familyId,
    name: rezept.title,
    normalized_name: normalizeName(rezept.title),
    servings: mapPortionen(rezept.servings),
    note: rezept.subtitle?.trim() ? rezept.subtitle.trim() : null,
    source_url: rezept.source_url,
    created_by: null,
    source: "spaisekammer",
    external_id: rezept.id,
    external_url: rezeptUrl(baseUrl, rezept.slug),
    external_updated_at: rezept.updated_at,
    owner_name: rezept.owner_name,
    cover_url: rezept.cover_url,
    updated_at: now,
    client_updated_at: now,
    deleted_at: null,
  };

  const bekannt = new Map(bestehendeZutaten.map((zutat) => [zutat.external_id, zutat]));
  const gesehen = new Set<string>();

  const ingredients = [...rezept.ingredients]
    .sort((a, b) => a.position - b.position)
    .map((zutat, index) => {
      const vorher = bekannt.get(zutat.id);
      gesehen.add(zutat.id);
      const einheit = mapEinheit(zutat.unit);
      // Ohne passende Einheit fällt auch die Menge weg, und genau diese Menge
      // entscheidet, ob Mehl eine Kleinigkeit oder der Hauptdarsteller ist.
      const menge = zutat.unit && einheit === null ? null : (zutat.amount ?? null);

      const row: RecipeIngredient & Record<string, unknown> = {
        id: vorher?.id ?? neueId(),
        family_id: familyId,
        recipe_id: recipe.id,
        product_id: null,
        name: zutat.item,
        normalized_name: normalizeName(zutat.item),
        // "2 EL Olivenöl" wird "Olivenöl". Mit Einheit bleibt die Menge,
        // ohne Einheit auch (2 Eier).
        quantity: menge,
        unit: einheit,
        category: mapWarengruppe(zutat.shopping_category, zutat.item),
        // Beim ersten Mal entscheidet das Grundwissen, danach das Gerät:
        // "Immer im Haus" ist die einzige Änderung, die hier erlaubt ist.
        // Die Warengruppe der Spaisekammer zählt mit, sie weiß von Gewürzen,
        // die in keiner Tabelle stehen können.
        is_staple: vorher
          ? vorher.is_staple
          : zutat.shopping_category?.trim().toLowerCase() === "spices" ||
            guessStaple({ name: zutat.item, quantity: menge, unit: einheit }),
        // Gruppen fallen weg, die Reihenfolge bleibt.
        sort_index: index,
        external_id: zutat.id,
        updated_at: now,
        client_updated_at: now,
        deleted_at: null,
      };
      return row;
    });

  const entfernt = bestehendeZutaten.filter((zutat) => !gesehen.has(zutat.external_id)).map((zutat) => zutat.id);

  return { recipe, ingredients, entfernt };
}

/** Wie alt ein Abgleich sein darf, bevor das Öffnen von "Planen" einen neuen anstößt. */
export const ABGLEICH_FRISCH_MINUTEN = 5;

/**
 * Soll beim Öffnen von "Planen" still abgeglichen werden?
 *
 * Nur mit Verbindung, nur online, und nur wenn der letzte Abgleich älter als
 * fünf Minuten ist. Sonst kostet jeder Wechsel auf den Reiter einen Aufruf,
 * und der Webhook macht den Alltag ohnehin sofortig.
 */
export function abgleichFaellig(input: { verbunden: boolean; online: boolean; lastSyncAt: IsoTimestamp | null; now?: Date }): boolean {
  const { verbunden, online, lastSyncAt } = input;
  if (!verbunden || !online) return false;
  if (lastSyncAt === null) return true;

  const stand = Date.parse(lastSyncAt);
  if (!Number.isFinite(stand)) return true;
  return (input.now ?? new Date()).getTime() - stand > ABGLEICH_FRISCH_MINUTEN * 60 * 1000;
}
